/**
 * DKG Manager for iframe
 *
 * Handles Distributed Key Generation inside secure iframe context
 * Uses existing dkls23-wasm module and TSS backend
 */

import init, * as tss from 'dkls23-wasm';
import { keccak256 } from 'viem';
import type { DKGResult } from './types';
import { TokenRefreshApiClient } from './token-refresh-client';

export class DKGManager extends TokenRefreshApiClient {
  private wasmLoaded: boolean = false;

  constructor() {
    super();
    console.log('[iframe][DKG] Initialized with TSS URL:', this.tssUrl);
  }

  /**
   * Initialize WASM module
   */
  async initialize(): Promise<void> {
    if (this.wasmLoaded) return;

    try {
      await init();
      this.wasmLoaded = true;
      console.log('[iframe][DKG] ✅ WASM module loaded');

      // Store WASM API globally for access
      (globalThis as any).__tss_wasm_api__ = tss;
      (globalThis as any).__tss_wasm_active__ = true;

      // Log memory info if available
      if (typeof (tss as any).wasm_memory_info === 'function') {
        try {
          const memInfo = (tss as any).wasm_memory_info();
          console.log('[iframe][DKG] WASM memory:', memInfo);
        } catch {}
      }
    } catch (error) {
      this.wasmLoaded = true;
      console.error('[iframe][DKG] Failed to load WASM:', error);
      throw new Error('Failed to initialize TSS WASM module');
    }
  }

  /**
   * Perform complete DKG protocol
   */
  async performDKG(userId: string, projectId: string, accessToken?: string): Promise<DKGResult> {
    if (!this.wasmLoaded) {
      await this.initialize();
    }

    console.log('[iframe][DKG] Starting DKG for user:', userId, 'projectId:', projectId, 'hasToken:', !!accessToken);

    // DKG Parameters (2-of-2 threshold signature)
    const CLIENT_PARTY_ID = 1;
    const THRESHOLD = 2;
    const TOTAL_PARTIES = 2;
    const DOMAIN_LABEL = this.generateDomainLabel(userId);

    // Step 1: Client starts DKG locally
    const clientStartResult = (tss as any).dkg_start(
      CLIENT_PARTY_ID,
      THRESHOLD,
      TOTAL_PARTIES,
      DOMAIN_LABEL
    );

    if (!Array.isArray(clientStartResult) || clientStartResult.length < 2) {
      throw new Error('Invalid dkg_start result format');
    }

    let clientState = clientStartResult[0];
    const clientMsg1 = clientStartResult[1];

    console.log('[iframe][DKG] Client started, sending to server...');

    // Step 2: Start DKG on server
    const serverStartResponse = await this.apiCall('POST', `/api/tss/${userId}/dkg/start`, {
      threshold: THRESHOLD,
      parties: TOTAL_PARTIES,
      domainLabel: DOMAIN_LABEL,
    }, projectId, accessToken);

    const { sessionId, msg1: serverMsg1 } = serverStartResponse;

    if (!serverMsg1?.kind || !serverMsg1.data_b64) {
      throw new Error('Invalid server msg1 format');
    }

    console.log('[iframe][DKG] Server started, sessionId:', sessionId);

    // Step 3: Round 1 - Exchange messages
    console.log('[iframe][DKG] Round 1: Exchanging messages...');

    const round1Response = await this.apiCall('POST', `/api/tss/${userId}/dkg/round`, {
      sessionId,
      clientMsg: clientMsg1,
    }, projectId, accessToken);

    const serverMsg2 = this.extractServerMessage(round1Response);

    const clientR2Result = (tss as any).dkg_handle(
      JSON.stringify({ bytes_b64: clientState.bytes_b64 }),
      JSON.stringify(serverMsg1)
    );

    clientState = clientR2Result[0];
    const clientMsg2 = clientR2Result[1];

    // Step 4: Round 2
    console.log('[iframe][DKG] Round 2: Exchanging messages...');

    const round2Response = await this.apiCall('POST', `/api/tss/${userId}/dkg/round`, {
      sessionId,
      clientMsg: clientMsg2,
    }, projectId, accessToken);

    const serverMsg3 = this.extractServerMessage(round2Response);

    const clientR3Result = (tss as any).dkg_handle(
      JSON.stringify({ bytes_b64: clientState.bytes_b64 }),
      JSON.stringify(serverMsg2)
    );

    clientState = clientR3Result[0];
    const clientMsg3 = clientR3Result[1];

    // Step 5: Round 3 (Final)
    console.log('[iframe][DKG] Round 3 (Final): Getting public key...');

    const finalResponse = await this.apiCall('POST', `/api/tss/${userId}/dkg/round`, {
      sessionId,
      clientMsg: clientMsg3,
    }, projectId, accessToken);

    if (!finalResponse.publicKey && !finalResponse.pubkey) {
      throw new Error('No public key in final response');
    }

    const publicKey = finalResponse.publicKey || finalResponse.pubkey;

    // Step 6: Final client processing to get keyshare
    console.log('[iframe][DKG] Processing final client keyshare...');

    const clientFinalResult = (tss as any).dkg_handle(
      JSON.stringify({ bytes_b64: clientState.bytes_b64 }),
      JSON.stringify(serverMsg3)
    );

    if (!Array.isArray(clientFinalResult) || clientFinalResult.length < 3) {
      throw new Error('Invalid final dkg_handle result format');
    }

    const clientFinish = clientFinalResult[2];

    if (!clientFinish?.keyshare_b64) {
      throw new Error('No keyshare_b64 in final result');
    }

    // Derive Ethereum address from public key
    const ownerAddress = this.deriveAddress(publicKey);

    console.log('[iframe][DKG] ✅ DKG completed successfully');
    console.log('[iframe][DKG] Owner address:', ownerAddress);

    return {
      keyshare: clientFinish,
      ownerAddress,
      sessionId,
    };
  }

  /**
   * Derive Ethereum address from uncompressed public key
   */
  private deriveAddress(publicKeyBase64: string): `0x${string}` {
    // Convert base64 to bytes
    const b64decoded = atob(publicKeyBase64);
    const bytes = new Uint8Array(b64decoded.length);
    for (let i = 0; i < b64decoded.length; i++) {
      bytes[i] = b64decoded.charCodeAt(i);
    }

    // Remove 0x04 prefix (uncompressed key indicator)
    const pubkeyBytes = bytes.slice(1);

    // Convert to hex
    const pubkeyHex = Array.from(pubkeyBytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    // Keccak256 hash
    const hash = keccak256(`0x${pubkeyHex}`);

    // Take last 20 bytes (40 hex chars)
    const addr = hash.slice(-40);

    // Apply EIP-55 checksum
    return this.toChecksumAddress(`0x${addr}`);
  }

  /**
   * Apply EIP-55 checksum to address
   */
  private toChecksumAddress(address: string): `0x${string}` {
    const addr = address.toLowerCase().replace('0x', '');
    const encoder = new TextEncoder();
    const addrBytes = encoder.encode(addr);
    const hash = keccak256(addrBytes).replace('0x', '');

    let checksumAddress = '0x';
    for (let i = 0; i < addr.length; i++) {
      checksumAddress += parseInt(hash[i], 16) >= 8 ? addr[i].toUpperCase() : addr[i];
    }

    return checksumAddress as `0x${string}`;
  }

  /**
   * Generate secure domain label for DKG
   */
  private generateDomainLabel(userId: string): string {
    const timestamp = Date.now();
    const randomBytes = new Uint8Array(16);
    crypto.getRandomValues(randomBytes);
    const randomHex = Array.from(randomBytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    return `dkls23:2of2:aa:${userId}:${timestamp}:${randomHex}`;
  }

  /**
   * Extract server message from response
   */
  private extractServerMessage(response: any): any {
    return response?.msgNext || response?.msg_next || response?.msg || null;
  }
}
